import {
  ParamsForMethod,
  useInboxConversation,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { withKey } from "@tellescope/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDraftAutosave, useDraftMutations, useInboxThreadDraft, type DraftCreateFields } from "../../hooks";
import { pendo } from "../../../../tracking";
import { MessageCompositionFooter } from "../../../../components/MessageCompositionFooter";
import { DraftAutosaveIndicator } from "../../../../components/DraftAutosaveIndicator";
import { buildSmsMessageWithFiles } from "../../utils";

export interface InboxThreadSMSCompositionFooterProps {
  threadId: string;
  conversationData?: ReturnType<typeof useInboxConversation>;
}

export const InboxThreadSMSCompositionFooter = withKey(
  ({ threadId, conversationData }: InboxThreadSMSCompositionFooterProps) => {
    const { t } = useTranslation("inbox");
    const [composerKey, setComposerKey] = useState(0);

    const internalConversationData = useInboxConversation(threadId, { enabled: !conversationData });
    const resolvedConversationData = conversationData ?? internalConversationData;
    const { metadata, messages } = resolvedConversationData;
    const enduserId = metadata?.enduserIds.at(0);
    const enduserPhoneNumber = metadata?.enduserPhoneNumber;
    const phoneNumber = metadata?.phoneNumber;

    const {
      queryParams,
      setQueryParams,
      matchingDraft,
      isEditingScheduled,
      sendAtInPast,
      shouldQueryDraft,
      autoSelectedDraft,
    } = useInboxThreadDraft({ conversationData: resolvedConversationData });

    const draftQuery = useTellescopeQuery({
      model: "sms_messages",
      method: "getOne",
      request: queryParams.draftId ?? "",
      enabled: shouldQueryDraft,
    });

    const latestDraft = useMemo(() => {
      if (queryParams.draftId) {
        return matchingDraft?.content ?? draftQuery.data?.message;
      }
      return autoSelectedDraft?.content;
    }, [autoSelectedDraft, draftQuery.data, matchingDraft, queryParams.draftId]);

    const autosaveDraftId = queryParams.draftId ? matchingDraft?.id ?? queryParams.draftId : autoSelectedDraft?.id;

    const buildDraftFields = useCallback((): DraftCreateFields | undefined => {
      if (!enduserId) return undefined;
      return { channel: "SMS", enduserId, enduserPhoneNumber, phoneNumber, inbound: false };
    }, [enduserId, enduserPhoneNumber, phoneNumber]);

    const autosave = useDraftAutosave({
      loadedContent: latestDraft,
      draftId: autosaveDraftId,
      isEditingScheduled,
      buildCreateFields: buildDraftFields,
    });

    const { sendDraft: sendDraftMutation } = useDraftMutations();

    const metadataForSendRequest = useMemo<
      Omit<ParamsForMethod<"sms_messages", "createOne">, "message"> | undefined
    >(() => {
      if (!enduserId) return undefined;
      return {
        enduserId,
        enduserPhoneNumber,
        phoneNumber,
        newThread: messages.length === 0,
        inbound: false,
      };
    }, [enduserId, enduserPhoneNumber, phoneNumber, messages.length]);

    const flushAutosave = autosave.flush;
    useEffect(() => flushAutosave, [flushAutosave]);

    const onMutationSuccess = () => {
      setComposerKey((k) => k + 1);
      setQueryParams((prev) => ({ ...prev, draftId: undefined }));
    };

    const sendMessageMutation = useTellescopeMutation({
      model: "sms_messages",
      method: "createOne",
      onSuccess: onMutationSuccess,
    });

    const updateMessageMutation = useTellescopeMutation({
      model: "sms_messages",
      method: "updateOne",
      onSuccess: onMutationSuccess,
    });

    const canSend = !!metadata && !!metadataForSendRequest && !sendMessageMutation.isPending;

    return (
      <MessageCompositionFooter
        key={composerKey}
        placeholder={t("Type your reply...")}
        autoFocus
        submitOnEnter={!isEditingScheduled}
        initialMessage={latestDraft}
        canSend={canSend}
        isUpdatePending={updateMessageMutation.isPending}
        enduserId={enduserId}
        channelType="sms"
        isEditingScheduled={isEditingScheduled}
        sendAtInPast={sendAtInPast}
        initDate={matchingDraft?.sendAt}
        scheduleDialogKey={matchingDraft?.sendAt?.toISOString() ?? "no-init-date"}
        onMessageChange={autosave.onMessageChange}
        leadingActions={<DraftAutosaveIndicator status={autosave.status} />}
        onSubmit={(payload) => {
          if (!metadataForSendRequest) return;
          pendo.track("messaging-sms-thread-compose-send");
          const draftId = autosave.takeDraftForSend();
          const message = buildSmsMessageWithFiles(payload.message, payload.files);
          const draftFields = buildDraftFields();

          if (draftId && draftFields) {
            sendDraftMutation({ id: draftId, message, fields: draftFields }).then(onMutationSuccess);
            return;
          }
          sendMessageMutation.mutate({ message, ...metadataForSendRequest });
        }}
        onSchedule={(payload, sendAt) => {
          if (matchingDraft) {
            updateMessageMutation.mutate({
              id: matchingDraft.id,
              sendAt,
              message: buildSmsMessageWithFiles(payload.message, payload.files),
            });
          } else if (metadataForSendRequest) {
            sendMessageMutation.mutate({
              message: buildSmsMessageWithFiles(payload.message, payload.files),
              sendAt,
              ...metadataForSendRequest,
            });
          }
        }}
        onSendNow={(payload) => {
          if (!matchingDraft) return;
          updateMessageMutation.mutate({
            id: matchingDraft.id,
            message: buildSmsMessageWithFiles(payload.message, payload.files),
            sendAt: new Date(),
          });
        }}
        onSaveScheduled={(payload) => {
          if (!matchingDraft) return;
          updateMessageMutation.mutate({
            id: matchingDraft.id,
            message: buildSmsMessageWithFiles(payload.message, payload.files),
          });
        }}
        onReschedule={(payload, sendAt) => {
          if (!matchingDraft) return;
          updateMessageMutation.mutate({
            id: matchingDraft.id,
            sendAt,
            message: buildSmsMessageWithFiles(payload.message, payload.files),
          });
        }}
        trackingIds={{
          schedule: "sms-thread-schedule-message-dialog-open",
          reschedule: "sms-thread-reschedule-message-dialog-open",
        }}
      />
    );
  },
  "threadId"
);
